import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IAppState } from "redux/reducers";
import { connect } from "react-redux";
import { accountService } from "api/services/account-service";
import {
  AccountType,
  accountTypeDict,
  AuthenticationMode,
  ssoProviderDictionary,
} from "api/entities/account";
import {
  MicrosoftAuthButton,
  MicrosoftIcon,
} from "components/account-actions/external-auth/buttons/microsoft-auth-button";
import { SettingsTab } from "components/shared-ui/tabs/tabs";
import Button from "components/shared-ui/button";
import { useApi } from "utils/hooks/use-api";
import { ChangeSetPasswordPanel } from "./panels/change-set-password-panel";
import styles from "./account-tab.module.scss";
import { SetupMfaPanel } from "./panels/mfa-setup-panel";
import {
  getAbsoluteRoute,
  MFARouteAction,
  SETTINGS_ROUTE,
} from "../../../../constants/routes";

import { DefaultRouteParam } from "../../../../constants/routes";
import { RecoveryCodesDialog } from "./dialogs/recovery-codes-dialog";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { commonActions } from "redux/actions/common";
import { usersThunkActions } from "redux/actions/users";

const mapStateToProps = (state: IAppState) => ({
  user: state.users.currentUser,
  loginFailed: state.account.loginFailed,
  loginError: state.account.loginError,
});

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, void, Action>) {
  return {
    showSnackBar: (message: string) => {
      return dispatch(commonActions.showSnackBar(true, message));
    },
    getCurrentUser: () => dispatch(usersThunkActions.getCurrentUser()),
  };
}

interface IAccountTabProps
  extends
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {
  activeTab: SettingsTab;
}

export const AccountTab = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: IAccountTabProps) => {
  const navigate = useNavigate();
  const { idOrAction } = useParams<{
    [DefaultRouteParam.IdOrAction]: string;
  }>();
  const isVerificationRoute =
    idOrAction === MFARouteAction.Verification && props.user.needsMfaSetup;

  const [api] = useApi();
  const [authenticationMode, setAuthenticationMode] =
    useState<AuthenticationMode>(props.user.authenticationMode);
  const [hasExternalAccount, setHasExternalAccount] = useState<boolean>(
    !!props.user.externalAccount,
  );
  const [changeSetPasswordPanelOpen, setChangeSetPasswordPanelOpen] =
    useState<boolean>(false);
  const [manageMfa, setManageMfa] = useState<boolean>(false);

  const [recoveryCodes, setRecoveryCodes] = useState<string[]>();
  const [isRecoveryCodesDialogOpen, setRecoveryCodesDialogOpen] =
    useState<boolean>(false);

  const deactivateMfa = () => {
    api(accountService.disableMFA()).then(({ msg }) => {
      props.showSnackBar(msg);
      props.getCurrentUser();
    });
  };

  // Open MFA modal if route is verification
  useEffect(() => {
    if (isVerificationRoute) {
      setManageMfa(true);
    }
  }, [isVerificationRoute]);

  function getExternalAccountSection() {
    return (
      <section>
        <div className={styles.row}>
          <div>SSO:</div>
          <div>
            {!hasExternalAccount
              ? "None"
              : ssoProviderDictionary[props.user.externalAccount.ssoProvider]}
          </div>
        </div>

        <div className={styles["column-button"]}>
          {!hasExternalAccount ? (
            <MicrosoftAuthButton
              redirectUri={window.location.href}
              buttonText={"Attach Microsoft Account"}
            />
          ) : (
            <Button
              colors="primary"
              onClick={() => {
                api(accountService.detachExternalAccount()).then(() =>
                  setHasExternalAccount(false),
                );
              }}
              testId="button-sign-up"
            >
              <MicrosoftIcon />
              Detach Account
            </Button>
          )}
        </div>
      </section>
    );
  }

  const changeSetPasswordButtonText =
    authenticationMode === AuthenticationMode.SSOOnly
      ? "Set Password"
      : "Change Password";

  return (
    <>
      <section>
        <div className={styles.row}>
          <div>Name:</div>
          <div>{props.user.name}</div>
        </div>

        <div className={styles.row}>
          <div>Email:</div>
          <div>{props.user.email}</div>
        </div>

        <div className={styles.row}>
          <div>Type:</div>
          <div>{accountTypeDict[props.user.accountType]}</div>
        </div>

        {authenticationMode === AuthenticationMode.SSOOnly && (
          <div className={styles["authentication-mode-desc"]}>
            Your account is directly logged in from Single-Sign On
          </div>
        )}

        <div className={styles["column-button"]}>
          <Button
            colors="primary"
            onClick={() => setChangeSetPasswordPanelOpen(true)}
            testId="button-sign-up"
          >
            {changeSetPasswordButtonText}
          </Button>
        </div>

        {props.user.accountType === AccountType.Enterprise && (
          <div className={styles.row}>
            <div>Organization Name</div>
            <div>{props.user.organization}</div>
          </div>
        )}
      </section>
      <section>
        <div className={styles.row}>
          <div>MFA:</div>
          <div>
            {props.user.mfaEnabled
              ? props.user.needsMfaSetup
                ? "Needs Verification"
                : "Enabled"
              : "Disabled"}
          </div>
        </div>
        <div className={styles["column-button"]}>
          {!props.user.mfaEnabled ? (
            <Button
              colors="primary"
              onClick={() => setManageMfa(true)}
              testId="button-manage-mfa"
            >
              Activate MFA Device
            </Button>
          ) : (
            <Button
              colors="extreme"
              onClick={deactivateMfa}
              testId="button-manage-mfa"
            >
              Deactivate MFA Device
            </Button>
          )}
        </div>
      </section>

      {getExternalAccountSection()}

      <SetupMfaPanel
        open={manageMfa}
        onClose={(codes?: string[]) => {
          setManageMfa(false);
          props.getCurrentUser();

          if (codes) {
            setRecoveryCodes(codes);
            setRecoveryCodesDialogOpen(true);
          }
        }}
        required={isVerificationRoute}
      />
      <ChangeSetPasswordPanel
        open={changeSetPasswordPanelOpen}
        onClose={(newAuthenticationMode: AuthenticationMode) => {
          setChangeSetPasswordPanelOpen(false);
          setAuthenticationMode(newAuthenticationMode);
        }}
      />
      <RecoveryCodesDialog
        open={isRecoveryCodesDialogOpen}
        onClose={() => setRecoveryCodesDialogOpen(false)}
        recoveryCodes={recoveryCodes}
      />
    </>
  );
});
